import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { requestOtp, logout, isAuthenticated, staff } = useAuth();
  const navigate = useNavigate();
  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const isAdmin = perms.includes('admin:full') || String(staff?.role || '').toLowerCase() === 'admin';
  const canAccessTempleDashboard = isAdmin || perms.includes('reports:view') || perms.includes('temple:access') || perms.includes('analytics:view');

  // On mount, if the user somehow got to /login, clear any existing session
  useEffect(() => {
    (async () => {
      try {
        await logout();
      } catch {}
    })();
  }, [logout]);

  // Default authenticated landing should be the temple dashboard for dashboard/report users.
  useEffect(() => {
    if (isAuthenticated) {
      const dest = canAccessTempleDashboard ? '/dashboard' : '/welcome';
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, canAccessTempleDashboard, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }
    
    // Check reCAPTCHA if enabled
    if (RECAPTCHA_ENABLED && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }
    
    try {
      setLoading(true);
      await requestOtp(email.trim(), recaptchaToken);
      // Reset reCAPTCHA after successful request
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      // Navigate to OTP page - user must check email for code
      navigate('/otp', { state: { email: email.trim() } });
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
      // Reset reCAPTCHA on error so user can try again
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError(''); // Clear any previous errors when reCAPTCHA is completed
  };

  return (
    <PageLayout contentMaxWidth="sm" showHeaderImage={false} showOrgHeader showAdminActions={false}>
      <Typography variant="h5" gutterBottom>Login</Typography>
      <Box component="form" onSubmit={onSubmit}>
        <TextField
          type="email"
          fullWidth
          label="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          helperText="Enter your email address to receive an OTP code"
          autoComplete="email"
          required
        />
        {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
        {RECAPTCHA_ENABLED && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
              onExpired={() => setRecaptchaToken(null)}
              onError={() => {
                setRecaptchaToken(null);
                setError('reCAPTCHA error. Please refresh the page and try again.');
              }}
            />
          </Box>
        )}
        <Button 
          variant="contained" 
          type="submit" 
          disabled={loading || !email.trim() || (RECAPTCHA_ENABLED && !recaptchaToken)} 
          sx={{ mt: 2 }}
        >
          {loading ? 'Sending...' : 'Send OTP'}
        </Button>
      </Box>
      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 3, textAlign: 'center', fontStyle: 'italic' }}>
        Authorized ICCT use only. By logging in you agree not to share or distribute devotee information or portal content outside the organization.
      </Typography>
    </PageLayout>
  );
};

export default LoginPage;
